import { useReactiveVar } from '@apollo/client';
import { Dropdown, theme } from 'aimo-ui';
import {
  CheckIcon,
  DropdownIcon,
  VEHICLE_INPUT_ICON_SIZE,
} from 'components/account/vehicle/VehicleIcons';
import Icon from 'components/icons/Icon';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { featureCountryCode } from 'states/persistInStorage';
import {
  CountryCode,
  Vehicle,
  VehicleCountryCode,
} from 'types/generatedSchemaTypes';
import {
  getSelectedCountryItem,
  getVehicleCountryItems,
  VehicleCountryItem,
} from 'utils/vehicleUtils';
import { useUiStore } from 'stores/uiStore';

export type VehicleCountrySelectorProps = {
  selectedCountry?: VehicleCountryCode | 'other';
  setSelectedCountry: (values: Partial<Vehicle>) => void;
  isVehicleNew?: boolean;
};

type IconComponentProps = {
  country: string;
};

const getFlagCountryCode = (country?: string) => {
  switch (country) {
    case 'FIN':
      return 'FI';
    case 'S':
      return 'SE';
    case 'N':
      return 'NO';
    default:
      return undefined;
  }
};

const appCountryToVehicleCountryCodeMap: {
  [key in CountryCode]: VehicleCountryCode;
} = {
  FI: 'FIN',
  SE: 'S',
  NO: 'N',
};

export const IconComponent = ({ country }: IconComponentProps) => {
  const flagCountryCode = getFlagCountryCode(country);
  return (
    <Icon
      name={flagCountryCode ? `flag-${flagCountryCode}` : 'Globe-Outlined'}
      size={VEHICLE_INPUT_ICON_SIZE}
      color={theme.colors.black}
      testID={
        flagCountryCode ? `${flagCountryCode}-icon-button` : 'globe-icon-button'
      }
    />
  );
};

const VehicleCountrySelector = ({
  selectedCountry,
  setSelectedCountry,
  isVehicleNew = true,
}: VehicleCountrySelectorProps) => {
  const { t } = useTranslation();
  const appCountry = useReactiveVar(featureCountryCode);
  const setCountryValue = useCallback(
    (countryCode: VehicleCountryCode) => {
      setSelectedCountry({
        licensePlate: {
          countryCode,
        } as any,
      });
    },
    [setSelectedCountry]
  );

  useEffect(() => {
    if (isVehicleNew && appCountry) {
      setCountryValue(appCountryToVehicleCountryCodeMap[appCountry]);
    }
  }, [isVehicleNew, appCountry, setCountryValue]);

  const mainCountries = useMemo(
    () => getVehicleCountryItems(t, IconComponent, true),
    [t]
  );

  const allCountryItems = useMemo(
    () => getVehicleCountryItems(t, IconComponent, false),
    [t]
  );

  const selectedItem = useMemo(
    () =>
      getSelectedCountryItem(selectedCountry, mainCountries, allCountryItems),
    [selectedCountry, mainCountries, allCountryItems]
  );

  const onPress = (item: VehicleCountryItem) => {
    if (item.value === 'other') {
      return useUiStore.getState().setSheetModal('otherCountrySelection');
    }
    setCountryValue(item.value as VehicleCountryCode);
  };
  return (
    <Dropdown
      items={mainCountries}
      defaultItem={selectedItem}
      onSelect={onPress}
      placeholder={t('vehicle.form.country.label')}
      dropdownIcon={DropdownIcon}
      checkIcon={CheckIcon}
      accessibilityLabel={t('vehicle.form.country.label')}
    />
  );
};

export default React.memo(VehicleCountrySelector);
